import React, { Fragment, useEffect, useState } from 'react'
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
} from 'chart.js';
import { ApiConstants } from '../../constants/ApiConstants';
import useMakeRequest from '../../utils/ApiHelper';
import moment from 'moment';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ChoresGraph = () => {
    const choresGraph = useMakeRequest();
    const [selectedType, setSelectedType] = useState<string>('weekly');
    const formattedLabels = (choresGraph?.data?.data?.graph?.days_or_dates?.length ?
        (selectedType === 'weekly' ?
            choresGraph?.data?.data?.graph?.days_or_dates :
            (choresGraph?.data?.data?.graph?.days_or_dates || []).map((date: any) =>
                moment(date).format('MM/DD/YYYY')
            )) : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);

    const data = {
        labels: formattedLabels,
        datasets: [
            {
                label: 'Chores',
                data: choresGraph?.data?.data?.graph?.hours || [100, 500, 250, 525, 540, 200, 490],
                backgroundColor: '#4ECB71',
                borderWidth: 0,
                borderRadius: {
                    topLeft: 8,
                    topRight: 8,
                },
            },
        ],
    };

    const options: any = {
        responsive: true,
        maintainAspectRatio: true,
        animation: {
            animateScale: true,
            animateRotate: true,
        },
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                enabled: true,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 10,
                cornerRadius: 6,
                titleColor: '#fff',
                bodyColor: '#fff',
            },
        },
        scales: {
            x: {
                ticks: {
                    color: '#000',
                    font: {
                        size: 14,
                        weight: '400',
                    },
                },
                grid: {
                    display: false,
                    drawBorder: false,
                },
                border: {
                    display: false,
                },
            },
            y: {
                beginAtZero: true,
                max: 550,
                ticks: {
                    stepSize: 275,
                    color: '#7C828A',
                    font: {
                        size: 13,
                    },
                    callback: function (value: any) {
                        return value;
                    },
                },
                grid: {
                    color: '#E5E5E5',
                    drawBorder: false,
                    lineWidth: 1,
                    borderDash: [5, 5],
                },
                border: {
                    display: false,
                },
            },
        },
        barThickness: 16,
        maxBarThickness: 20,
    };

    useEffect(() => {
        choresGraph.makeRequest({ url: ApiConstants?.ChoresGraph, payload: { type: selectedType } })
    }, [selectedType])

    const choresValues = data.datasets[0].data;

    return (
        <section className='dash-graph-area' aria-labelledby='chores-graph-heading'>
            <div className='page-padding-x'>
                <div className='d-flex align-items-center'>
                    <h2 id='chores-graph-heading' className='dash-graph-area-title'>Chores Graph</h2>
                    <div className='graph-tabs ms-auto' role='tablist' aria-label='Chores graph time frame'>
                        {
                            [
                                { title: 'Week', type: 'weekly' },
                                { title: 'Month', type: 'monthly' }
                            ].map(({ title, type }) => {
                                const isActive = selectedType === type;
                                return (
                                    <button
                                        key={type}
                                        type="button"
                                        role="tab"
                                        aria-selected={isActive}
                                        className={isActive ? 'active' : ''}
                                        onClick={() => setSelectedType(type)}
                                    >
                                        {title}
                                    </button>
                                )
                            })
                        }
                    </div>
                </div>

                <div
                    tabIndex={0}
                    className='graph-container pt-4'
                    role='region'
                    aria-label={`Chores Graph: ${selectedType} data`}
                >
                    <div className='visually-hidden' tabIndex={0} aria-label={`Chores Graph data summary for ${selectedType} period`}>
                        <p>Chores data for {selectedType} period:</p>
                        <table>
                            <caption>Chores completed data</caption>
                            <thead>
                                <tr>
                                    <th scope="col">Period</th>
                                    <th scope="col">Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formattedLabels.map((label: string, idx: number) => (
                                    <tr key={idx}>
                                        <td>{label}</td>
                                        <td>{choresValues[idx] ?? 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div aria-hidden="true">
                        <Bar data={data} options={options} />
                    </div>
                </div>
            </div>
        </section>
    )
}

export default ChoresGraph